import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Row, Col, Typography, Button, Result, Carousel, Space, Image } from 'antd';
import { LeftOutlined, RightOutlined, WhatsAppOutlined } from '@ant-design/icons';
import PujaSpinner from '../../components/PujaSpinner';
import { SiteHeader, SiteFooter } from './RematePage';
import {
  tipografiaDe, altoHeaderDe, columnasValidas, NEUTROS as T,
} from '../../utils/micrositioTema';
import { heroBackground, heroTextLayout, heroTextPadding } from '../../utils/hero';
import { fixUrl, catalogoPublicoService, analyticsService } from '../../services/api';
import { useCentroVisibleDelPadre } from './useCentroVisibleDelPadre';
import { useAvisarAltoAlPadre } from '../../hooks/useAvisarAltoAlPadre';
import { linksDelProductor, seccionesDelCatalogo } from '../../utils/menuCatalogo';
import { urlSegura } from '../../utils/urlSegura';
import { clickInterno } from '../../utils/navegacionSpa';
import BloqueToro, { ESTILOS_BLOQUE_TORO } from './components/BloqueToro';
import MosaicoHembrasOp2, { ESTILOS_MOSAICO_HEMBRAS } from './components/MosaicoHembrasOp2';
import BloqueToroOp2 from './components/BloqueToroOp2';
import BarraBienvenida from './components/BarraBienvenida';
import { useAltoHeaderMedido } from './useAltoHeaderMedido';

const { Title, Paragraph } = Typography;

/**
 * Los dos botones configurables de un bloque (hero o sección informativa).
 *
 * Un botón SIN TEXTO no se dibuja, y esa es la forma de apagarlo: no hay switch
 * aparte. Mismo criterio que la sección informativa entera — un interruptor que
 * convive con el contenido crea un segundo estado que se desincroniza.
 *
 * Un botón CON texto y SIN link tampoco se dibuja: es peor que no tenerlo,
 * porque promete una acción que no pasa nada al tocarla.
 */
const BotonesConfigurados = ({ config, prefijo, colorPrimario, colorSecundario, onAncla }) => {
  const definidos = [
    {
      // `slot` y no el texto para la `key` de React: el texto lo escribe el
      // productor y nada le impide poner "Contactar" en los dos botones. Dos
      // keys iguales entre hermanos hacen que React reuse el nodo equivocado.
      slot: 'primario',
      texto: config[`${prefijo}_btn_primario_texto`],
      url: config[`${prefijo}_btn_primario_url`],
      color: colorPrimario || '#1a5f2a',
      relleno: true,
    },
    {
      slot: 'secundario',
      texto: config[`${prefijo}_btn_secundario_texto`],
      url: config[`${prefijo}_btn_secundario_url`],
      color: colorSecundario || '#8B4513',
      relleno: false,
    },
  ].filter((b) => {
    if (!(b.texto || '').trim() || !(b.url || '').trim()) return false;
    // Un `#ancla` baja en esta misma página: no navega a ningún esquema.
    // Todo el resto pasa por la MISMA lista blanca que los links del menú, y
    // no es simetría cosmética: `hero_btn_*_url` e `info_btn_*_url` son texto
    // LIBRE del productor igual que `menu_links[].url` —default `''` en
    // `DEFAULT_CATALOGO_CONFIG`— y `set_config()` valida CLAVES, no valores.
    // Endurecer los menús y dejar estos cuatro botones era blindar la puerta
    // y dejar la ventana abierta, en el mismo archivo.
    //
    // Y el botón que no pasa NO SE DIBUJA, mismo criterio que un botón sin
    // texto: uno que promete una acción y no hace nada es peor que ninguno.
    return b.url.trim().startsWith('#') || urlSegura(b.url) !== null;
  });

  if (!definidos.length) return null;

  return (
    <Space wrap style={{ marginTop: 20 }}>
      {definidos.map((b) => (
        <Button
          key={b.slot}
          size="large"
          onClick={() => {
            const url = b.url.trim();
            if (url.startsWith('#')) onAncla(url.slice(1));
            // Ya pasó la lista blanca de esquemas en el filtro de arriba: si
            // no la hubiera pasado, este botón no estaría dibujado.
            // `noopener` NO es opcional: sin él la pestaña que se abre puede
            // tocar `window.opener` y redirigir ESTA página.
            else window.open(url, '_blank', 'noopener,noreferrer');
          }}
          style={{
            background: b.relleno ? b.color : 'transparent',
            borderColor: b.color,
            color: b.relleno ? '#fff' : b.color,
            fontWeight: 600,
          }}
        >
          {b.texto}
        </Button>
      ))}
    </Space>
  );
};

/**
 * El filete que va debajo del título de cada sección.
 *
 * Usa el color SECUNDARIO y no el primario a propósito: el primario ya está en
 * los chips de raza, en las barras de DEP y en los botones. Un acento que
 * aparece en todos lados deja de acentuar; el secundario existe justamente
 * para esto.
 *
 * `minWidth` además del 10%: en una pantalla angosta, el 10% de 360px son 36
 * pixeles — un guioncito que no se lee como remate del título.
 */
const FileteTitulo = ({ color }) => (
  <div
    style={{
      width: '10%',
      minWidth: 56,
      height: 3,
      background: color || '#8B4513',
      marginBottom: 20,
      borderRadius: 2,
    }}
  />
);

// El velo que protege el titulo de OP-2 cuando va apoyado sobre la foto: opaco
// arriba y transparente abajo, el texto se apoya en lo opaco y el animal queda
// limpio. Vive aca y no inline porque una cadena de gradiente adentro de un
// style es ilegible, y porque el dia que haya que ajustarla se busca por nombre.
//
// LO REGULA EL PRODUCTOR (2026-09-29): color, alto y si va o no, desde
// Estructura del panel. Depende de la foto —una toma clara con titulo oscuro
// no lo necesita; una oscura con titulo blanco necesita uno oscuro— y la foto
// la elige el, no el codigo. Devuelve `null` con el velo apagado: el que llama
// no dibuja la capa, en vez de dibujar una transparente.
//
// El color viaja como hex de 6 y la opacidad se le pega como dos digitos mas
// (#rrggbbaa): .92 → eb, .72 → b8, 0 → 00. Son las MISMAS tres paradas que
// tenia el velo fijo, para que un catalogo viejo se vea igual que ayer.
const hexDeSeis = (color) => {
  const hex = String(color || '').trim();
  // Un ColorPicker con alfa devuelve 9 caracteres; se toma la parte del color
  // y la opacidad la ponen las paradas, que es donde vive la forma del velo.
  if (/^#[0-9a-f]{8}$/i.test(hex)) return hex.slice(0, 7);
  return /^#[0-9a-f]{6}$/i.test(hex) ? hex : '#ffffff';
};

const veloCabeceraOp2 = (config) => {
  // `!== false` y no truthy: un catalogo guardado antes de que existiera la
  // clave no la tiene, y el default es CON velo, como siempre fue.
  if (config.op2_velo_on === false) return null;
  const c = hexDeSeis(config.op2_velo_color);
  return {
    background: `linear-gradient(to bottom, ${c}eb 0%, ${c}b8 45%, ${c}00 100%)`,
    // En px. El clamp es un cinturon contra un JSON editado a mano: la UI ya
    // limita el InputNumber a ese rango.
    height: Math.min(620, Math.max(80, Number(config.op2_velo_alto) || 280)),
  };
};

// Los dos fondos que se alternan. Gris apenas: lo que se busca es que se note
// DÓNDE termina una sección y empieza la otra, no dibujar cajas de colores.
const FONDO_A = '#ffffff';
const FONDO_B = '#f7f7f5';

/**
 * Una banda de la página: el FONDO cruza la pantalla y el CONTENIDO queda
 * centrado.
 *
 * Es al revés de como estaba. El `maxWidth: 1280` iba en el mismo nodo que el
 * `background`, así que el color se cortaba a 1280px: la sección "Nuestra
 * historia" quedaba como un bloque flotando en el medio, con dos franjas
 * blancas a los costados. Una banda cortada no se lee como sección, se lee
 * como caja.
 *
 * EL COLOR LO DECIDE LA POSICIÓN, no cuál sección es. Se alternan por índice,
 * así funciona con dos secciones y con cinco, y agregar una no obliga a
 * repintar las que ya estaban a mano.
 */
const Banda = ({ indice, id, children }) => (
  <section
    id={id}
    style={{ background: indice % 2 === 0 ? FONDO_A : FONDO_B }}
  >
    <div style={{ maxWidth: 1280, margin: '0 auto', padding: '56px 24px' }}>
      {children}
    </div>
  </section>
);

// Dónde se apoya la imagen superpuesta del hero. Es su propio eje, aparte del
// texto: el texto puede ir centrado y la marca a la izquierda, que es un
// encuadre clásico.
const OVERLAY_POS = {
  izquierda: { ml: 0, mr: 'auto' },
  centro: { ml: 'auto', mr: 'auto' },
  derecha: { ml: 'auto', mr: 0 },
};

// `hero_align` es el eje HORIZONTAL del texto del hero, y sale de la config del
// productor ('izquierda' | 'centro' | 'derecha'). Estaba definido en el modelo
// y no lo leia NADIE: el texto salia siempre pegado a la izquierda, con un
// padding fijo, aunque el catalogo estuviera configurado en 'centro'.
//
// Es aparte de `heroTextLayout()`, que resuelve la VERTICAL
// (`hero_text_align_v`). Dos ejes distintos, dos claves distintas.
const HERO_ALIGN = {
  izquierda: { justifyContent: 'flex-start', textAlign: 'left' },
  centro: { justifyContent: 'center', textAlign: 'center' },
  derecha: { justifyContent: 'flex-end', textAlign: 'right' },
};

// Las dos secciones, en el orden en que las declara la config. La lista es el
// orden REAL de render: una cabaña que vende sobre todo vientres puede poner
// hembras primero sin que eso sea un caso especial acá.
const SECCIONES = {
  toros: {
    onKey: 'toros_on',
    tituloKey: 'toros_titulo',
    subtituloKey: 'toros_subtitulo',
    tituloDefault: 'Toros padres',
  },
  hembras: {
    onKey: 'hembras_on',
    tituloKey: 'hembras_titulo',
    subtituloKey: 'hembras_subtitulo',
    tituloDefault: 'Hembras seleccionadas',
  },
};


// El hover en CSS y no con estado de React: con `onMouseEnter` cada entrada y
// salida del mouse re-renderiza la tarjeta —con su imagen— para mover un pixel.
// EXPORTADA junto con `AnimalCard`. La tarjeta NO se dibuja sola: sin esta hoja
// pierde el radio, la sombra y —lo que mas se nota— el `overflow: hidden` que
// recorta la foto contra las esquinas. En el listado de seccion aparecio
// exactamente asi: la misma tarjeta, con la foto cuadrada y desbordando.
//
// Van juntas a proposito. Quien importe una tiene que importar la otra, y por
// eso el nombre esta al lado en el mismo `export`.
export const ESTILOS_CARD = `
.pj-card {
  background: ${T.surface};
  border: 1px solid ${T.border};
  border-radius: ${T.radius}px;
  box-shadow: 0 1px 3px rgba(35,35,35,.05);
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: box-shadow .15s, transform .15s;
}
.pj-card:hover { box-shadow: 0 4px 12px rgba(35,35,35,.08); transform: translateY(-1px); }
`;

// A NIVEL DE MÓDULO, como `SiteHeader` y `SiteFooter`. Definido adentro del
// render, cada cambio de estado del padre lo convierte en un componente nuevo y
// React remonta todas las tarjetas — con sus imágenes.
// EXPORTADA: la usa tambien `SeccionCatalogoPage`, el listado completo de una
// seccion. Es la MISMA tarjeta a proposito — el comprador que toca "ver todos"
// tiene que reconocer lo que venia viendo en la portada, no encontrarse con
// otra tarjeta.
//
// Se exporta desde aca y no se muda a `components/` porque su hoja de estilos
// (`ESTILOS_CARD`) y su grilla viven en este archivo; mudarla es un trabajo
// aparte. Importar entre paginas del micrositio es el patron que el repo ya usa
// (`SiteHeader`/`SiteFooter` salen de `RematePage`), y aca ademas es
// INTRA-dominio: las dos paginas son del catalogo.
export const AnimalCard = ({ animal, config, tipografia, slug }) => {
  const navigate = useNavigate();
  const acento = config.color_primary || '#1a5f2a';
  const colorTitulo = config.color_title || '#1a1a1a';
  const imagen = animal.imagen_thumb || animal.imagen;

  return (
    <>
      <div className="pj-card">
        <div style={{ aspectRatio: '4 / 3', background: T.pearl, overflow: 'hidden' }}>
          {imagen ? (
            <Image
              // En la tarjeta va el THUMB y en grande la de alta: mostrar la
              // pesada en una grilla de veinte hembras son veinte fotos de
              // tamaño completo que nadie está mirando todavía.
              src={fixUrl(imagen)}
              preview={{
                src: fixUrl(animal.imagen || imagen),
                mask: <span style={{ fontSize: 12 }}>Ver en grande</span>,
              }}
              alt={animal.nom_animal || animal.rp_animal || 'Animal'}
              loading="lazy"
              wrapperStyle={{ width: '100%', height: '100%', display: 'block' }}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          ) : null}
        </div>

        {/* ── Cabecera: nombre + chip de raza ────────────────────────── */}
        <div
          style={{
            display: 'flex', justifyContent: 'space-between',
            alignItems: 'flex-start', gap: 10, padding: '14px 16px 0',
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: 15, fontWeight: 800, letterSpacing: '-.005em',
                lineHeight: 1.25, fontFamily: tipografia.title, color: colorTitulo,
              }}
            >
              {/* El nombre CORTO. El de pedigrí —"PILAGAS 34642 MAGO 21524 XPO
                  TE"— ocupa tres renglones en una tarjeta y no lo lee nadie de
                  un vistazo. El backend ya cae a `nom_animal` si no está. */}
              {animal.nombre_corto || animal.nom_animal || animal.rp_animal || 'Sin nombre'}
            </div>
            {animal.nombre_corto && animal.nom_animal !== animal.nombre_corto && (
              <div style={{ fontSize: 10.5, color: T.textDim, marginTop: 2 }}>
                {animal.nom_animal}
              </div>
            )}
            <div style={{ fontSize: 11.5, color: T.textDim, marginTop: 3, fontWeight: 500 }}>
              {[
                animal.nom_categoria,
                animal.rp_animal && animal.nom_animal ? `RP ${animal.rp_animal}` : null,
              ].filter(Boolean).join(' · ') || '\u2014'}
            </div>
          </div>
          {animal.nom_raza && (
            /* EL MISMO CHIP DE RAZA QUE EL BLOQUE DE TORO Y QUE LA FICHA:
               radio 4, sin borde, fondo tenue del acento, sin mayusculas.
               Quedo atras cuando se unificaron los otros dos —vive en otro
               archivo, dentro de `AnimalCard`— y en la MISMA portada el toro
               mostraba el chip nuevo y la hembra la pildora vieja.

               Va como comentario JS y no como el de llaves de JSX: adentro de
               una expresion "&& (" el de llaves es sintaxis de HIJOS y rompe el
               parseo. Y OJO con escribir el cierre de comentario dentro del
               texto — termina el bloque antes de tiempo. */
            <span
              style={{
                flex: 'none', fontSize: 11.5, fontWeight: 700, letterSpacing: '.03em',
                background: `${acento}14`, color: acento, borderRadius: 4,
                padding: '2px 8px', whiteSpace: 'nowrap',
              }}
            >
              {animal.nom_raza}
            </span>
          )}
        </div>

        {/* `cantidad_crias` es un dato CARGADO, no calculado: las crías reales
            incluyen animales que nunca pasaron por Puja, así que no se deriva
            ni con la genealogía completa. Va sólo si está — un "0 crías"
            afirma que no tuvo, cuando lo que pasa es que no se cargó. */}
        {animal.cantidad_crias ? (
          <div style={{ padding: '12px 16px 0' }}>
            <div style={{ fontSize: 21, fontWeight: 800, lineHeight: 1 }}>
              {animal.cantidad_crias}
            </div>
            <div
              style={{
                fontSize: 10.5, fontWeight: 700, letterSpacing: '.04em',
                textTransform: 'uppercase', color: T.textDim, marginTop: 3,
              }}
            >
              Crías
            </div>
          </div>
        ) : null}

        {/* ── Pie ────────────────────────────────────────────────────── */}
        <div
          style={{
            marginTop: 'auto', display: 'flex', alignItems: 'center',
            justifyContent: 'space-between', gap: 8,
            margin: '14px 16px 0', padding: '10px 0 14px',
            borderTop: `1px solid ${T.border}`,
          }}
        >
          <span style={{ fontSize: 11, fontWeight: 700, color: T.textDim }}>
            {animal.apto_comercializar ? (
              <span style={{ color: T.success }}>● Disponible</span>
            ) : (
              'En el plantel'
            )}
          </span>
          <a
            href={`/genetica/${slug}/animal/${animal.slug}`}
            // Rutea en vez de recargar, igual que en `BloqueToro` y en la
            // ficha. Este era el TERCER anchor interno y se habia quedado sin
            // interceptar: la grilla de hembras es la superficie de
            // comparacion de mas trafico del catalogo, asi que era justo
            // donde mas dolia bajar el bundle de nuevo y reiniciar el
            // handshake de alto del iframe.
            onClick={clickInterno(navigate, `/genetica/${slug}/animal/${animal.slug}`)}
            style={{
              fontSize: 12.5, fontWeight: 700, color: acento,
              display: 'flex', alignItems: 'center', gap: 4,
            }}
          >
            Ver ficha <RightOutlined style={{ fontSize: 10 }} />
          </a>
        </div>
      </div>
    </>
  );
};


// Los puntos del carrusel. Las FLECHAS no salen de acá: se dibujan aparte,
// ver `FlechaCarrusel`.
const ESTILOS_CARRUSEL = `
.pj-carrusel .slick-dots { bottom: -10px; }
.pj-carrusel .slick-dots li button { background: #232323 !important; }
@media (max-width: 991px) { .pj-flecha { display: none !important; } }
`;

/**
 * La flecha del carrusel, con LA MISMA FORMA que la navegación entre animales
 * del micrositio de remate (`NavSideBtn` en AnimalPage): pegada al borde de la
 * pantalla, del color del productor, redondeada sólo del lado que mira hacia
 * adentro.
 *
 * Se dibuja APARTE y no con `arrows` de antd. Las de antd se posicionan
 * relativas al slide y con `adaptiveHeight` ese slide cambia de alto en cada
 * transición, así que quedaban a distinta altura según qué toro se estuviera
 * mirando. Este control se ancla al CONTENEDOR del carrusel, que no cambia.
 *
 * `absolute` y NO `fixed`. La primera versión copió la forma de `NavSideBtn`
 * (AnimalPage), que es fija porque navega entre PÁGINAS y tiene que estar
 * siempre a mano. Acá es otra cosa: pegadas al viewport, quedaban flotando
 * sobre toda la página, y al scrollear hasta las hembras seguían ahí — el
 * comprador tocaba una flecha y hacía pasar toros que ya no estaba viendo.
 * Un control tiene que vivir donde vive lo que controla.
 *
 * En mobile no se dibuja: a 400px de ancho tapa la foto, y quedan los puntos.
 */
const FlechaCarrusel = ({ hacia, color, onClick, oculta }) => {
  // Oculta y no deshabilitada: así se ve dónde empieza y dónde termina la
  // lista, en vez de dejar una flecha gris que no hace nada.
  if (oculta) return null;
  const esIzq = hacia === 'prev';
  return (
    <button
      type="button"
      className="pj-flecha"
      aria-label={esIzq ? 'Anterior' : 'Siguiente'}
      onClick={onClick}
      style={{
        position: 'absolute',
        top: '50%',
        // AL BORDE DE LA PANTALLA, saliendo del contenedor centrado.
        //
        // `(100% - 100vw) / 2` es la distancia desde el borde del contenido
        // hasta el del viewport: con un contenedor de 1232px en una pantalla de
        // 1920 da -344, que es exactamente lo que hay que correrse. Se recalcula
        // solo al cambiar el ancho, sin JavaScript.
        //
        // `absolute` y no `fixed`: fija al viewport la flecha quedaba flotando
        // sobre toda la página y, al scrollear hasta las hembras, seguía ahí
        // haciendo pasar toros que ya nadie miraba.
        [esIzq ? 'left' : 'right']: 'calc((100% - 100vw) / 2)',
        transform: 'translateY(-50%)',
        width: 36,
        height: 72,
        border: 'none',
        cursor: 'pointer',
        background: color,
        color: '#fff',
        zIndex: 200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: esIzq ? '0 8px 8px 0' : '8px 0 0 8px',
      }}
    >
      {esIzq ? <LeftOutlined /> : <RightOutlined />}
    </button>
  );
};


const SeccionAnimales = ({ clave, datos, config, tipografia, slug, indice, plantilla }) => {
  // El boton "Ver todos" navega al listado completo de la seccion, asi que este
  // componente necesita su propio `navigate`: el que habia en el archivo vive
  // adentro de `AnimalCard`, que es otro componente.
  const navigate = useNavigate();
  // EL MISMO COLOR QUE EL BOTON DE "NUESTRA HISTORIA", con la misma caida que
  // usa `BotonesConfigurados` para su primario. No es el `acento`: el acento es
  // `color_primary` —en Pilaga el gris oscuro— y el boton de accion del
  // productor sale de `color_btn_primary`, que es el naranja. Dos botones de
  // accion del mismo sitio pintados de colores distintos se leen como dos
  // cosas distintas.
  const colorBoton = config.color_btn_primary || config.color_primary || '#1a5f2a';
  const carruselRef = useRef(null);
  // Qué slide se está mirando, para saber si la flecha va o no va. Los hooks
  // van ANTES del early-return de abajo: llamarlos condicionalmente rompe el
  // orden de hooks entre renders.
  const [slide, setSlide] = useState(0);

  const meta = SECCIONES[clave];
  // UNA CLAVE DESCONOCIDA NO TIRA LA PÁGINA. `secciones_orden` es config JSON
  // que edita el productor: alcanza un typo, o una sección que se renombre en
  // el código y quede vieja en la config de un catálogo, para que `meta` venga
  // `undefined` — y leerle `.onKey` es un TypeError, o sea PANTALLA BLANCA en
  // una página pública. Mismo criterio que `posicionMedida` en la ficha: lo que
  // no está listado se ignora, no rompe.
  if (!meta) return null;

  // `!== false` y no un truthy pelado: la clave puede no existir todavía en la
  // config de un catálogo viejo, y ahí el default es MOSTRAR.
  if (!datos || config[meta.onKey] === false || datos.total === 0) return null;

  const titulo = config[meta.tituloKey] || meta.tituloDefault;
  const subtitulo = config[meta.subtituloKey];
  const hayMas = datos.total > datos.destacados.length;
  // El clamp compartido de `micrositioTema.js`, que es donde vive el por qué.
  // Acá estaba reimplementado inline —con la constante declarada DENTRO del
  // componente, recreada en cada render— justo en el archivo que el docstring
  // de ese util nombra como uno de sus dos consumidores.
  const columnas = columnasValidas(config);

  // LOS TOROS NO VAN EN GRILLA, y esa es la diferencia de tratamiento que pide
  // el wireframe: cada toro ocupa su propio bloque, con la foto grande, la
  // tarjeta de DEPs y dos secundarias. Las hembras sí van en grilla.
  //
  // No es capricho de diseño: un toro padre es la decisión de compra más cara
  // de una cabaña y se mira de a uno; una hembra se elige comparando contra las
  // otras, y para comparar hace falta verlas juntas.
  const enBloques = clave === 'toros';

  // EN OP-2 LAS DOS SECCIONES SE DIBUJAN DISTINTO, y se decide UNA vez aca
  // para que lo lean la cabecera, el cuerpo y el return:
  //   - la BANDA de toros: la cabecera flota sobre la foto y el "Ver todos"
  //     baja debajo de la tarjeta;
  //   - el MOSAICO de hembras: la cabecera es la primera celda, con los
  //     filtros, y el "Ver todas" va centrado debajo (piezas 2 y 3 de OP-2).
  const bandaOp2 = plantilla === 'op2' && enBloques;
  const mosaicoOp2 = plantilla === 'op2' && clave === 'hembras';

  // EL BOTON "VER TODOS", armado una sola vez porque va en DOS lugares segun
  // la plantilla: al lado del titulo (OP-1, y las hembras en las dos) o debajo
  // de la tarjeta del toro (la banda de OP-2). `null` si no hay mas que
  // mostrar, asi el que lo recibe no tiene que preguntar.
  //
  // EL TEXTO NOMBRA LO QUE HAY DEL OTRO LADO —"Ver todos los toros"— y no el
  // titulo que configuro el productor. Ese titulo es texto libre ("TOROS
  // DISPONIBLES", "NUESTROS PADRES") y meterlo adentro del boton daria frases
  // como "Ver todos los NUESTROS PADRES".
  const botonVerTodos = hayMas ? (
    <Button
      size="large"
      onClick={() => navigate(`/genetica/${slug}/${clave}`)}
      style={{
        flex: 'none',
        // RELLENO Y TEXTO BLANCO, calcado del primario de
        // `BotonesConfigurados` —el de "Nuestra Historia"—: mismo
        // `size="large"`, mismo `fontWeight: 600`, mismo par
        // fondo/borde. Antes era un contorno del acento y parecia un
        // boton de otra familia.
        background: colorBoton,
        borderColor: colorBoton,
        color: '#fff',
        fontFamily: tipografia.body,
        fontWeight: 600,
      }}
    >
      {clave === 'toros' ? 'Ver todos los toros' : 'Ver todas las hembras'}
      <RightOutlined style={{ fontSize: 11 }} />
    </Button>
  ) : null;

  // LA CABECERA DE LA SECCION, en una constante porque las dos plantillas la
  // dibujan en lugares distintos: OP-1 la pone arriba, en el flujo; OP-2 la
  // apoya ENCIMA de la foto.
  const cabecera = (
    <>
      {/* EL TITULO Y EL "VER TODOS" EN LA MISMA FILA. El boton estaba al final
          de la seccion, despues de la grilla: para tocarlo habia que pasar por
          todos los destacados, que es justo lo que uno NO quiere hacer cuando
          decide que prefiere ver la lista completa.

          SALVO EN LA BANDA DE OP-2: ahi la cabecera flota arriba a la izquierda
          y la tarjeta abajo a la derecha, y el boton al lado del titulo quedaba
          a media pantalla de lo que amplia. Va debajo de la tarjeta, que es
          donde `BloqueToroOp2` lo recibe como `pie` (2026-09-29).

          `align-items: baseline` y no `center`: el titulo es grande y el boton
          chico, y centrarlos deja al boton flotando a media altura de la letra
          en vez de apoyado en la misma linea. */}
      <div
        style={{
          display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
          gap: 16, flexWrap: 'wrap', marginBottom: 12,
        }}
      >
        <Title
          level={2}
          style={{
            fontFamily: tipografia.title,
            color: config.color_title || '#1a1a1a',
            margin: 0,
          }}
        >
          {titulo}
        </Title>

        {!bandaOp2 && !mosaicoOp2 && botonVerTodos}
      </div>
      <FileteTitulo color={config.color_secondary} />
      {/* EL SUBTITULO VIAJA CON LA CABECERA y no con el cuerpo. En OP-2 la
          cabecera es un overlay `absolute` y el cuerpo arranca en y=0: un
          subtitulo dejado en el cuerpo caia JUSTO DEBAJO del titulo, tapado por
          el degradado. El productor lo escribia y no lo veia nunca, sin nada
          que le dijera por que. */}
      {subtitulo && (
        <Paragraph
          style={{
            fontFamily: tipografia.body,
            color: config.color_paragraph || '#666',
            fontSize: 16,
            lineHeight: 1.6,
            marginBottom: 0,
          }}
        >
          {subtitulo}
        </Paragraph>
      )}
    </>
  );

  const cuerpo = (
    <>
      {enBloques ? (
        <div style={{ position: 'relative' }}>
          {/* CARRUSEL, no una pila. Los bloques de toro son altos: apilados,
              cuatro toros son cuatro pantallas de scroll antes de llegar a las
              hembras, y el comprador no llega. De a uno además es como se mira
              un toro padre: comparando contra el anterior, no de un vistazo.

              `adaptiveHeight` porque los bloques NO miden lo mismo — uno con
              dos fotos secundarias y DEPs es mucho más alto que uno pelado, y
              sin esto antd fija el alto del primero y recorta a los demás. */}
          <Carousel
          ref={carruselRef}
          className="pj-carrusel"
          adaptiveHeight
          dots={datos.destacados.length > 1}
          // `infinite` apagado a propósito: con dos o tres toros, el loop deja
          // al comprador sin saber si dio la vuelta o si hay más. Con las
          // flechas que desaparecen en los extremos, el largo se ve.
          infinite={false}
          // `beforeChange` Y NO `afterChange`, y esto no es preferencia: con
          // `afterChange` las flechas quedaban UN PASO ATRÁS.
          //
          // antd fuerza `waitForAnimate = false` (`antd/lib/carousel/index.js`,
          // sobreescribe el `true` de react-slick), y eso activa esta rama en
          // `@ant-design/react-slick/lib/inner-slider.js`:
          //
          //     if (!waitForAnimate && this.animationEndCallback) {
          //       clearTimeout(this.animationEndCallback);
          //       afterChange && afterChange(currentSlide);  // <- el VIEJO
          //     }
          //
          // O sea: si el comprador clickea antes de que termine la animación
          // —500ms por default— se CANCELA el timeout que iba a reportar el
          // índice nuevo y se dispara `afterChange` con el anterior. Pasando
          // rápido de un toro a otro, `prev` seguía escondida en el segundo y
          // el tercero, y recién aparecía al frenar en el último, cuando el
          // último timeout alcanzaba a disparar con el índice de verdad.
          //
          // `beforeChange(desde, hacia)` se llama SINCRÓNICO al arrancar la
          // transición y `hacia` siempre es el destino real. De paso las
          // flechas reaccionan al toque en vez de a los 500ms.
          //
          // Los dos juntos NO: el `afterChange` viejo le pisa el valor bueno.
          beforeChange={(_desde, hacia) => setSlide(hacia)}
          style={{ paddingBottom: 8 }}
        >
          {datos.destacados.map((animal) => (
            <div key={animal.id_animal}>
              {/* El wrapper con padding va ACA y no en el bloque: el slide de
                  antd tiene overflow oculto, y una sombra dibujada al ras del
                  borde se corta justo en el hover.

                  EN OP-2 NO VA: ese bloque ocupa el ancho completo de la
                  pantalla, y 4px de padding le dejan una franja blanca a cada
                  lado. La sombra que el padding protege es la de la tarjeta de
                  OP-1; la de OP-2 vive adentro, lejos del borde. */}
              <div style={plantilla === 'op2' ? undefined : { padding: '4px 4px 8px' }}>
                {/* LA PLANTILLA ELIGE LA DISPOSICION, no el contenido. Los
                    dos bloques muestran el mismo toro con los mismos cuatro
                    DEPs: OP-1 pone la ficha al lado de la foto, OP-2 se la
                    monta encima en una tarjeta flotante.

                    Se eligen por componente y no con `if` adentro de uno solo
                    porque son dos layouts enteros; un componente con las dos
                    disposiciones cruzadas de condicionales no lo lee nadie. */}
                {plantilla === 'op2' ? (
                  <BloqueToroOp2
                    animal={animal}
                    config={config}
                    tipografia={tipografia}
                    slug={slug}
                    pie={botonVerTodos}
                  />
                ) : (
                  <BloqueToro
                    animal={animal}
                    config={config}
                    tipografia={tipografia}
                    slug={slug}
                  />
                )}
              </div>
            </div>
          ))}
          </Carousel>
          <FlechaCarrusel
            hacia="prev"
            color={config.color_primary || '#1a5f2a'}
            onClick={() => carruselRef.current?.prev()}
            oculta={slide === 0}
          />
          <FlechaCarrusel
            hacia="next"
            color={config.color_primary || '#1a5f2a'}
            onClick={() => carruselRef.current?.next()}
            oculta={slide >= datos.destacados.length - 1}
          />
        </div>
      ) : (
        <Row gutter={[16, 16]}>
          {datos.destacados.map((animal) => (
            <Col xs={24} sm={12} md={8} lg={24 / columnas} key={animal.id_animal}>
              <AnimalCard animal={animal} config={config} tipografia={tipografia} slug={slug} />
            </Col>
          ))}
        </Row>
      )}

    </>
  );

  // OP-2 EN LA SECCION DE TOROS ROMPE LA BANDA. La foto deja de estar contenida
  // en los 1280px y ocupa el ancho completo de la pantalla, con el titulo y el
  // boton apoyados encima. No hace falta ningun truco de 100vw: la Banda YA es
  // un <section> a todo lo ancho — lo que limita a 1280 es su div interno, asi
  // que alcanza con no usarlo.
  //
  // Solo los TOROS. Las hembras siguen en grilla dentro del ancho contenido,
  // que es donde una grilla se lee.
  if (bandaOp2) {
    const velo = veloCabeceraOp2(config);
    return (
      <section id={clave} style={{ position: 'relative' }}>
        {/* EL VELO, como capa propia debajo de la cabecera y encima de la foto.
            No es decoracion: el titulo va en el color que eligio el productor
            y abajo puede haber un toro oscuro, un cielo blanco o un pastizal.
            Sin el velo el titulo se lee o no segun que foto subio, que es lo
            mismo que decir que a veces no se lee. Va SOLO arriba y se
            desvanece: protege el texto sin taparle el animal a nadie.

            Es una capa APARTE y no el fondo de la cabecera para que el
            productor pueda darle el alto que la foto pida sin mover el texto.
            `null` = lo apago desde el panel, y no se dibuja nada. */}
        {velo && <div className="pj-op2-velo" style={velo} role="presentation" />}
        <div className="pj-op2-cabecera">
          {/* `pointerEvents` vuelve a auto adentro: la cabecera no puede
              comerse los clicks de lo que tiene debajo. */}
          <div className="pj-op2-cabecera-interior">{cabecera}</div>
        </div>
        {cuerpo}
      </section>
    );
  }

  // OP-2 EN LA SECCION DE HEMBRAS TAMBIEN ROMPE LA BANDA: el mosaico va al
  // borde de la pantalla, como la banda de toros. El fondo de la seccion se
  // mantiene (alterna por indice, como siempre) para que el aire de arriba y
  // de abajo del mosaico siga la secuencia de bandas de la pagina.
  if (mosaicoOp2) {
    return (
      <section id={clave} style={{ background: indice % 2 === 0 ? FONDO_A : FONDO_B, padding: '56px 0' }}>
        <MosaicoHembrasOp2
          datos={datos}
          config={config}
          tipografia={tipografia}
          slug={slug}
          cabecera={cabecera}
          pie={botonVerTodos}
          filtrosOn={config.hembras_filtros_on !== false}
        />
      </section>
    );
  }

  return (
    <Banda indice={indice} id={clave}>
      {cabecera}
      {cuerpo}
    </Banda>
  );
};


const CatalogoPage = () => {
  // Le avisa al sitio incrustante cuánto medimos, para que agrande el iframe en
  // vez de dejarlo clavado en los 800px del snippet. Y es la página que MÁS lo
  // necesita: el catálogo está pensado para incrustarse en el sitio que la
  // cabaña ya tiene, típicamente WordPress. Además pone `data-embed` en el
  // `<html>`, que es lo que activa `html[data-embed] .micrositio-page` en
  // `index.css` —de ahí la clase en el `<div>` de abajo, sin la cual el
  // `minHeight: 100vh` sigue en pie dentro del iframe y anida un scroll.
  useAvisarAltoAlPadre();

  // Y JUSTAMENTE POR LA LINEA DE ARRIBA hace falta esta. Cuando el padre
  // agranda el iframe hasta que entra todo, el viewport DEL IFRAME pasa a medir
  // lo mismo que el documento entero y `position: fixed` deja de significar
  // "pegado a la pantalla". El padre nos dice que franja esta mirando el
  // usuario. `null` cuando no hay padre o el snippet es viejo.
  // Ver `useCentroVisibleDelPadre`.
  const centroVisible = useCentroVisibleDelPadre();

  const { slug } = useParams();
  // Para los items del menú, que ahora navegan a la ficha de cada animal en
  // vez de bajar todos al ancla de la sección.
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Un 404 y un 500 NO son la misma pantalla. Ver el `catch` de abajo.
  const [error, setError] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    let vivo = true;
    setLoading(true);
    // Se limpia al cambiar de slug: sin esto, el error de un catalogo apagado
    // queda pegado y tapa al siguiente que SI carga.
    setError(null);
    (async () => {
      try {
        const res = await catalogoPublicoService.get(slug);
        if (vivo) setData(res.data);

        // La visita a la portada del catalogo. Hasta la Tanda 4 esto no
        // existia: `analytics.id_evento` era NOT NULL y una visita de
        // catalogo no tenia de donde colgar, asi que no se registraba nada.
        //
        // catch vacio a proposito, igual que en el micrositio del remate: el
        // tracking es fire-and-forget. Si falla analytics, la persona igual
        // tiene que poder ver el catalogo — molestarla con un error por una
        // metrica seria peor que perder la metrica.
        const idCatalogo = res.data?.catalogo?.id_catalogo_genetica;
        if (vivo && idCatalogo) {
          analyticsService.trackCatalogo('visita_index', idCatalogo).catch(() => {});
        }
      } catch (err) {
        // SIN `message.error` a proposito: no es una accion puntual que falla,
        // es la carga entera de la pantalla, y eso se comunica con el `<Result>`
        // a pagina completa de mas abajo. Un toast ADEMAS seria ruido
        // duplicado. Es la excepcion (d) de las reglas del front.
        //
        // PERO EL ERROR NO SE TIRA A LA BASURA. Antes el `catch` era pelado y
        // los dos modos de falla colapsaban en "este catalogo no esta
        // disponible": si el backend se caia con un 500, el productor leia que
        // su catalogo no existe y abria un ticket por algo transitorio. Un 404
        // es "no esta"; cualquier otra cosa es "volve a intentar". Misma
        // distincion que ya hace `RematePage`.
        if (vivo) {
          setData(null);
          setError(err.response?.status === 404 ? 'not_found' : 'server_error');
        }
      } finally {
        if (vivo) setLoading(false);
      }
    })();
    return () => { vivo = false; };
  }, [slug]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Baja al ancla del hash cuando se llega desde afuera: el menú de la FICHA
  // manda a `/genetica/:slug#toros`, porque desde ahí un scroll local no tiene
  // a dónde ir —las secciones viven en esta página, no en aquella—.
  //
  // Espera a `data` porque antes de que llegue el catálogo la sección todavía
  // no está en el DOM, y `scrollIntoView` sobre un elemento que no existe no
  // hace nada ni avisa. El `requestAnimationFrame` deja que React pinte antes
  // de medir.
  useEffect(() => {
    if (!data) return undefined;
    const ancla = window.location.hash.replace('#', '');
    if (!ancla) return undefined;
    const id = requestAnimationFrame(() => {
      document.getElementById(ancla)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    return () => cancelAnimationFrame(id);
  }, [data]);

  // El alto REAL de la cabecera. Se mide en vez de calcularse: el teórico sale
  // del tipo de cabecera, pero el de verdad cambia con el logo que subió el
  // productor, con el ancho de la pantalla y ahora también con la franja de
  // bienvenida de OP-1, que vive DENTRO de la cabecera. Un padding calculado
  // de menos deja el título tapado; de más, la franja blanca que veníamos
  // arrastrando. Compartido con `AnimalCatalogoPage` y `SeccionCatalogoPage` —
  // ver el porqué completo en `useAltoHeaderMedido.js`.
  const altoHeaderMedido = useAltoHeaderMedido(data);

  if (loading) return <PujaSpinner size="lg" label="Cargando catálogo…" fondo="#fff" />;

  if (!data) {
    // `<Result>` a pagina completa, que es el patron que ya usa el resto del
    // micrositio para una pantalla que no carga. El componente vacio de antd
    // esta pensado para una LISTA sin items, que es otra cosa.
    return error === 'server_error' ? (
      <Result
        status="500"
        title="No pudimos cargar el catálogo"
        subTitle="Intentá de nuevo en unos momentos."
      />
    ) : (
      <Result
        status="404"
        title="Este catálogo no está disponible"
        subTitle="El enlace puede ser incorrecto, o la cabaña todavía no lo publicó."
      />
    );
  }

  const config = data.catalogo.configuracion_micrositio || {};

  // Baja a una sección de esta misma página. Lo usan el menú y los botones
  // configurables, así que se define antes que los dos.
  const irA = (ancla) => {
    const el = document.getElementById(ancla);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  // Los dos salen de helpers compartidos y no de leer la config a mano: el
  // alto del header depende del TIPO de cabecera elegida, y la tipografía
  // tiene su default. Reimplementarlos acá era repetir dos decisiones que
  // el micrositio del remate ya tiene tomadas.
  const tipografia = tipografiaDe(config);
  // `altoHeaderDe` devuelve el alto TEÓRICO por tipo de cabecera, pero el real
  // depende del logo, de su tamaño y de si la barra bajó a dos filas por falta
  // de ancho. `altoHeaderMedido` mide el nodo; el teórico queda de respaldo
  // para el primer render, antes de que exista algo que medir.
  const altoHeader = altoHeaderMedido || altoHeaderDe(config);
  const plantilla = data.catalogo.plantilla_index || 'op1';

  // CABECERA Y PIE SE PUEDEN APAGAR, y eso no existe en el micrositio de un
  // remate. Este catálogo está pensado para incrustarse en el sitio que la
  // cabaña ya tiene —típicamente WordPress—, donde la página anfitriona aporta
  // su propia cabecera y su propio pie: dejarlos prendidos apila dos menús y
  // dos footers, uno adentro del otro.
  const conCabecera = config.cabecera_on !== false;
  const conPie = config.footer_on !== false;

  // El logo PROPIO del catálogo primero, y recién si no hay se cae al de la
  // identidad visual del cliente. Una cabaña puede querer su marca acá y el
  // logo de la empresa en los remates; el fallback está para que el que no
  // sube nada no se quede sin logo.
  const logoCrudo = config.logo_img || data.identidad?.logo_url || null;
  const logoUrl = logoCrudo ? fixUrl(logoCrudo) : null;

  // `SiteHeader` y `SiteFooter` piden un `evento` y de él usan SÓLO
  // `nombre_evento` (verificado: tres usos, todos de esa clave). Se les pasa el
  // nombre del catálogo en esa forma en vez de bifurcar dos componentes que ya
  // funcionan y están probados en producción.
  const comoEvento = { nombre_evento: data.catalogo.nombre || data.cliente?.razon_social };

  // LA NAVEGACION DEL CATALOGO, que no es la del remate.
  //
  // `SiteHeader` trae por defecto Inicio, Información, Condiciones y Contacto,
  // todos apuntando a `/remate/:slug/...`: en el catálogo esas rutas NO
  // EXISTEN y el comprador caía en un 404. Acá se le pasa la propia.
  //
  // Cada sección baja a su ancla y despliega sus animales. El item usa
  // `nombre_corto` —el nombre de fantasía— porque el de pedigrí no entra en un
  // menú: "PAYESERO MARCABOTE 51M PAYÉ 21570 MÁGICO T/E" ocupa la barra entera
  // y no distingue nada. El backend ya resolvió el fallback a `nom_animal`
  // para el que no lo cargó: largo, pero verdadero.
  // QUÉ SECCIONES SE DIBUJAN, calculado UNA sola vez. Lo consumen el menú y el
  // render de las bandas, y cada uno tenía su propia copia de este predicado —
  // el del menú acá, y el de `SeccionAnimales` en sus tres early-returns.
  const clavesVisibles = (config.secciones_orden || ['toros', 'hembras'])
    .filter((clave) => {
      const meta = SECCIONES[clave];
      // Una clave que no existe se ignora, no explota. El `!meta` va PRIMERO
      // porque si no se lee `.onKey` de `undefined` y se cae la portada entera.
      if (!meta) return false;
      // La sección no se dibuja si está apagada o si no tiene un solo animal:
      // un item que baja a una sección vacía es el mismo link muerto que se
      // estaba tratando de sacar.
      return config[meta.onKey] !== false && (data[clave]?.total || 0) > 0;
    });

  const seccionesMenu = clavesVisibles
    // Se normaliza a la forma que espera el util —la misma que el backend le
    // manda a la ficha— y el armado del menu es el MISMO en las dos pantallas.
    // Antes cada una tenia su copia, y los items de la portada bajaban todos al
    // ancla de la seccion en vez de ir a la ficha de su animal.
    .map((clave) => {
      // Ya pasó el filtro de arriba, así que acá `meta` existe.
      const meta = SECCIONES[clave];
      return {
        clave,
        titulo: config[meta.tituloKey] || meta.tituloDefault,
        animales: (data[clave]?.destacados || []).map((a) => ({
          slug: a.slug,
          nombre: a.nombre_corto,
        })),
      };
    });

  const navPropia = seccionesDelCatalogo(
    seccionesMenu,
    (animalSlug) => navigate(`/genetica/${slug}/animal/${animalSlug}`),
  );

  // Los links propios del productor van DESPUES de las secciones de animales:
  // el catálogo es de genética, así que lo primero del menú tiene que ser la
  // genética. El armado vive en `utils/menuCatalogo.js` porque la ficha del
  // animal dibuja el mismo menú y tenía una copia de este bloque; ahí está
  // también la lista blanca de esquemas de URL. Acá un `#ancla` baja en ESTA
  // misma página, que es la única diferencia entre las dos pantallas.
  navPropia.push(...linksDelProductor(config, (hash) => irA(hash.slice(1))));

  // EN OP-1 la barra de bienvenida es la FRANJA que va DENTRO de la cabecera
  // fija (`franjaSuperior` de `SiteHeader`, más abajo) y no algo que se dibuje
  // acá en el flujo. En OP-2 sigue siendo la BANDA de siempre, después del
  // hero — ver el porqué completo en el comentario de más abajo, donde se
  // colocan las dos.
  const franjaBienvenida = plantilla === 'op1'
    ? <BarraBienvenida config={config} tipografia={tipografia} variante="franja" />
    : null;

  // EL HERO ARRANCA SIEMPRE EN CERO, y el lugar para la cabecera lo hace el
  // padding de adentro. Nunca un margen.
  //
  // La cabecera es `position: fixed` (RematePage), o sea que NO ocupa lugar en
  // el flujo: el hero ya empieza debajo de ella sin que nadie haga nada. El
  // `marginTop` que se le ponía cuando la cabecera era sólida —el caso de
  // Pilagá, que la tiene negra— no compensaba nada: EMPUJABA el hero otro alto
  // de header hacia abajo, y esa era la banda blanca.
  //
  // Así el hero pasa por detrás de la cabecera siempre. Con una cabecera sólida
  // eso no se nota —tapa el borde superior de la foto, que es imagen— y con una
  // transparente es justamente lo que se busca.
  const heroPaddingTop = conCabecera ? altoHeader : 0;

  // ¿Hay algo que escribir sobre la foto? Un contenedor vacío no se ve, pero
  // ocupa lugar y corre el encuadre del hero hacia abajo.
  const heroConTexto = Boolean(
    (config.hero_titulo || '').trim()
    || (config.hero_subtitulo || '').trim()
    || (config.hero_overlay_img || '').trim()
    || (config.hero_btn_primario_texto || '').trim()
    || (config.hero_btn_secundario_texto || '').trim(),
  );

  const hero = (
    <section
      style={{
        position: 'relative',
        minHeight: config.hero_altura || 420,
        background: heroBackground(config, config.hero_img ? fixUrl(config.hero_img) : null),
        display: 'flex',
        ...heroTextLayout(config),
        // `heroTextPadding` recibe la BASE: con la cabecera transparente el
        // hero pasa por detrás, así que el texto tiene que empezar debajo de
        // ella. Sumarlo acá y no en un div aparte es lo que evita la franja.
        // El alto de la cabecera va SOLO arriba: es para que el texto EMPIECE
        // debajo de la cabecera transparente, no para separarlo del fondo.
        ...heroTextPadding(config, heroPaddingTop, 0),
        ...(HERO_ALIGN[config.hero_align] || HERO_ALIGN.centro),
        paddingLeft: 24,
        paddingRight: 24,
      }}
    >
      {/* El contenido del hero sale ENTERO de lo que el productor cargó. Antes
          caía al nombre del catálogo cuando `hero_titulo` estaba vacío, y eso
          dejaba un título que no se podía editar ni sacar desde ningún lado —
          el productor veía "Genética Pilagá" sobre su foto y no tenía cómo
          quitarlo. Ahora, sin nada cargado, el hero es sólo la imagen: el
          bloque entero no se dibuja en vez de dejar un contenedor vacío que
          igual empuja el layout. */}
      <div style={{ maxWidth: 900, display: heroConTexto ? 'block' : 'none' }}>
        {/* La imagen que se apoya ENCIMA del fondo, típicamente un PNG con
            transparencia. Va antes del título y con `objectFit: contain`: es
            una marca, no un fondo — recortarla o estirarla la arruina. */}
        {config.hero_overlay_img && (
          <img
            src={fixUrl(config.hero_overlay_img)}
            alt=""
            style={{
              display: 'block',
              width: '100%',
              maxWidth: config.hero_overlay_ancho || 320,
              height: 'auto',
              objectFit: 'contain',
              marginBottom: 16,
              // La posición sale de su propia clave y no se hereda de
              // `hero_align`: el texto puede ir centrado y la marca a la
              // izquierda, que es un encuadre clásico.
              marginLeft: OVERLAY_POS[config.hero_overlay_pos || 'centro'].ml,
              marginRight: OVERLAY_POS[config.hero_overlay_pos || 'centro'].mr,
            }}
          />
        )}
        <Title
          style={{
            color: config.color_hero_text || '#fff',
            fontFamily: tipografia.title,
            fontSize: config.hero_text_size || 32,
            textTransform: config.text_transform_hero || 'none',
            margin: 0,
          }}
        >
          {config.hero_titulo}
        </Title>
        {config.hero_subtitulo && (
          <Paragraph
            style={{
              color: config.color_hero_text || '#fff',
              fontFamily: tipografia.body,
              fontSize: 18,
              marginTop: 12,
            }}
          >
            {config.hero_subtitulo}
          </Paragraph>
        )}
        <BotonesConfigurados
          config={config}
          prefijo="hero"
          colorPrimario={config.color_btn_primary || config.color_primary}
          colorSecundario={config.color_btn_secondary || config.color_secondary}
          onAncla={irA}
        />
      </div>
    </section>
  );

  // El índice arranca DESPUÉS de la sección informativa si esa se dibuja: el
  // color sale de la posición real en la página, no del orden de este array.
  // Sin eso, con historia visible quedaban dos bandas blancas pegadas.
  const hayHistoria = Boolean(config.info_titulo || config.info_texto_1);

  // EL ÍNDICE SALE DE LO QUE SE DIBUJA, no de la posición en `secciones_orden`.
  //
  // `Banda` alterna el fondo con `indice % 2`. Antes se enumeraba el array de
  // config, así que una sección que NO se dibuja —clave desconocida, `*_on` en
  // false, o cero animales publicados— consumía su índice igual y las dos
  // bandas vecinas quedaban del MISMO color, pegadas. Es exactamente el bug que
  // el offset de `hayHistoria` vino a evitar, apareciendo por otra puerta.
  //
  // Y se llega sin tocar un solo switch: alcanza un catálogo que publique
  // hembras y todavía no tenga toros cargados.
  const secciones = clavesVisibles.map((clave, i) => (
    <SeccionAnimales
      plantilla={plantilla}
      key={clave}
      clave={clave}
      datos={data[clave]}
      config={config}
      tipografia={tipografia}
      slug={slug}
      indice={i + (hayHistoria ? 1 : 0)}
    />
  ));

  const historia = hayHistoria ? (
    // Sin switch de on/off a propósito, igual que en el modelo: aparece si el
    // productor la completó. Un toggle aparte crea un segundo estado que se
    // desincroniza — texto cargado, switch en No, y el productor jura que lo
    // escribió y no se ve.
    // SIN fondo propio. Salía de `color_cta_bg` —el tab Colores, "fondo de
    // secciones destacadas"— y en Pilagá ese color es un mostaza fuerte: el
    // bloque quedaba flotando en el medio de la pantalla, con dos franjas
    // blancas a los costados y el texto encima sin contraste. La separación
    // entre secciones la da ahora la alternancia de bandas.
    <Banda indice={0} id="historia">
      <Row gutter={[32, 24]} align="middle">
        <Col xs={24} md={config.info_img ? 12 : 24}>
          {config.info_titulo && (
            <>
              <Title
                level={2}
                style={{
                  fontFamily: tipografia.title,
                  color: config.color_title,
                  marginBottom: 12,
                }}
              >
                {config.info_titulo}
              </Title>
              <FileteTitulo color={config.color_secondary} />
            </>
          )}
          {[config.info_texto_1, config.info_texto_2].filter(Boolean).map((t, i) => (
            <Paragraph
              key={i}
              // 16px es el piso que pide Google para "texto legible en mobile",
              // y el default de antd es 14. Por debajo de eso, Lighthouse lo
              // marca y —más importante— se lee peor en un teléfono al sol,
              // que es donde un cabañero mira esto.
              style={{
                fontFamily: tipografia.body,
                color: config.color_paragraph,
                fontSize: 16,
                lineHeight: 1.6,
              }}
            >
              {t}
            </Paragraph>
          ))}
          <BotonesConfigurados
            config={config}
            prefijo="info"
            colorPrimario={config.color_btn_primary || config.color_primary}
            colorSecundario={config.color_btn_secondary || config.color_secondary}
            onAncla={irA}
          />
        </Col>
        {config.info_img && (
          <Col xs={24} md={12}>
            <img
              src={fixUrl(config.info_img)}
              alt=""
              style={{ width: '100%', borderRadius: 8, display: 'block' }}
            />
          </Col>
        )}
      </Row>
    </Banda>
  ) : null;

  // MISMO BOTON, LO UNICO QUE CAMBIA ES CONTRA QUE SE POSICIONA — igual que
  // las flechas de `AnimalPage`.
  //
  // Standalone (`centroVisible` en null) se queda `fixed` abajo a la derecha,
  // que es lo correcto y lo que hubo siempre. Incrustado, ese `bottom: 20` no
  // es 20px del borde de la pantalla: es 20px del final del catalogo ENTERO,
  // pasado el hero, los toros y las veinte hembras. El comprador podia recorrer
  // la pagina completa sin ver nunca el unico camino al WhatsApp de la cabaña,
  // que es exactamente lo que este boton existe para evitar.
  //
  // Va al CENTRO de lo visible y no abajo porque el snippet manda el centro y
  // nada mas — y el snippet queda pegado en el sitio del productor, asi que
  // pedirle el alto obligaria a que TODOS lo re-peguen para que el boton
  // aparezca. Asi anda con el que ya tienen.
  const whatsappSigueAlPadre = centroVisible != null;
  const whatsapp = data.catalogo.whatsapp_consultas ? (
    <a
      href={`https://wa.me/${data.catalogo.whatsapp_consultas.replace(/\D/g, '')}`}
      target="_blank"
      rel="noopener noreferrer"
      style={whatsappSigueAlPadre
        ? {
          position: 'absolute',
          top: centroVisible,
          // `right` con `absolute` resuelve contra el bloque contenedor
          // inicial —ningun ancestro esta posicionado— y el documento ocupa
          // el 100% del ancho del iframe: es el mismo borde de siempre.
          right: 20,
          transform: 'translateY(-50%)',
          zIndex: 900,
        }
        : { position: 'fixed', right: 20, bottom: 20, zIndex: 900 }}
    >
      <Button type="primary" shape="round" size="large" icon={<WhatsAppOutlined />}>
        Consultar
      </Button>
    </a>
  ) : null;

  return (
    <div className="micrositio-page" style={{ background: '#fff', minHeight: '100vh' }}>
      {/* LAS HOJAS DE ESTILO, UNA VEZ CADA UNA. Estaban dibujadas adentro de los componentes
          que las usan: `ESTILOS_CARD` dentro de `AnimalCard` —o sea un `<style>`
          idéntico por cada hembra de la grilla, y son veinte—, la del bloque de
          toro dentro de `BloqueToro` —una por toro del carrusel— y la del
          carrusel una por sección. No rompía nada, pero son nodos repetidos por
          dato, y el criterio ya estaba escrito en `RematePage`
          (`WA_SHARE_BTN_STYLES`: "Insertado UNA vez") y aplicado bien en la
          ficha del animal. */}
      <style>{ESTILOS_CARD}</style>
      <style>{ESTILOS_CARRUSEL}</style>
      <style>{ESTILOS_BLOQUE_TORO}</style>
      <style>{ESTILOS_MOSAICO_HEMBRAS}</style>
      {conCabecera && (
        <SiteHeader
          config={config}
          tipografia={tipografia}
          logoUrl={logoUrl}
          evento={comoEvento}
          scrolled={scrolled}
          isAuthenticated={false}
          slug={slug}
          navPropia={navPropia}
          rutaInicio={`/genetica/${slug}`}
          franjaSuperior={franjaBienvenida}
        />
      )}

      {/* UNA de las diferencias entre las plantillas: dónde entra la barra de
          bienvenida. En OP-1 es la FRANJA fina que se le pasó recién a
          `SiteHeader` por `franjaSuperior` —vive DENTRO de la cabecera fija,
          no acá abajo—; en OP-2 es la BANDA de siempre, después del hero, y
          sólo en la portada. La OTRA diferencia está en `SeccionAnimales`,
          que en OP-2 saca la sección de toros de la banda y la lleva a ancho
          completo con la foto de fondo.

          LA FRANJA DE OP-1 ESTUVO ROTA DESDE SIEMPRE Y NADIE LA VIO: antes se
          dibujaba EN ESTE LUGAR, antes de `{hero}`, en el flujo normal del
          documento. Pero la cabecera es `position: fixed; zIndex: 1000`
          (`headerStyle` en `RematePage.jsx`), así que NO ocupa lugar en el
          flujo: lo primero que hay en el flujo queda en y=0, DETRÁS de la
          cabecera. La franja nunca se vio, en ningún catálogo OP-1, y el
          wireframe (`op-1.jpg`) sí la muestra arriba de todo — por eso ahora
          se dibuja DENTRO del header, que es el único lugar donde una franja
          "arriba de la cabecera" se ve de verdad estando la cabecera fija.

          Lo que NO cambia entre plantillas es QUÉ PÁGINAS EXISTEN: la ficha y
          el listado de sección son los mismos en las dos. Si la plantilla
          decidiera eso, cambiarla le rompería al productor las URLs que ya
          compartió, y en silencio. */}
      {hero}
      {plantilla === 'op2' && (
        <BarraBienvenida config={config} tipografia={tipografia} variante="banda" />
      )}

      {/* La sección informativa va ANTES de los animales, como en el wireframe:
          es la presentación de la cabaña, y presentarse después de mostrar la
          mercadería es al revés. Antes quedaba al fondo, debajo de todo. */}
      {historia}
      {secciones}

      {conPie && (
        <SiteFooter
          config={config}
          tipografia={tipografia}
          evento={comoEvento}
          categorias={[]}
          logoUrl={logoUrl}
          slug={slug}
          navPropia={navPropia}
        />
      )}

      {whatsapp}
    </div>
  );
};

export default CatalogoPage;
